import React from 'react';
import {
  isObject,
  getItemId,
  areItemsEqual,
  EXPERIENCE_ID_MATCH_FIELDS,
  HOTEL_ID_MATCH_FIELDS,
  TEMPLATE_ID_MATCH_FIELDS,
} from '../helpers';
import {
  isProgram,
  isProgramSuggestedOption,
  isTranslation,
  isPeriod,
  isAttachment,
} from '../typeCheckers';
import {
  EmptyValue,
  SmartItemDisplay,
  ProgramSuggestedOptionDiff,
  TranslationDiffDisplay,
  MealDisplay,
} from '../displays';
import ObjectDiff from './ObjectDiff';
import ProgramDiff from './ProgramDiff';
import PeriodDiff from './PeriodDiff';

export default function ArrayDiff({
  beforeValue,
  afterValue,
  depth = 0,
  viewOnly = false,
  attachmentLabels,
  labels,
  fieldName,
}) {
  const before = Array.isArray(beforeValue) ? beforeValue : [];
  const after = Array.isArray(afterValue) ? afterValue : [];

  // Check if this is an array of primitives
  const isPrimitiveArray =
    (before.length > 0 && !isObject(before[0])) ||
    (after.length > 0 && !isObject(after[0]));

  // Build lookup for before items - by ID for objects, by value for primitives
  const beforeLookup = new Map();
  before.forEach((item, index) => {
    if (isPrimitiveArray) {
      const key = String(item);
      beforeLookup.set(key, { item, index });
    } else {
      const id = getItemId(item, fieldName);
      if (id) {
        beforeLookup.set(id, { item, index });
      }
    }
  });

  // In view-only mode, show all items
  if (viewOnly) {
    if (after.length === 0) {
      return <EmptyValue />;
    }

    // Use MealDisplay for meals
    if (fieldName === 'meals') {
      return (
        <div className="flex flex-wrap gap-2">
          {after.map((item, index) => (
            <MealDisplay key={item || index} meal={item} />
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {after.map((item, index) => (
          <SmartItemDisplay
            key={getItemId(item) || index}
            item={item}
            attachmentLabels={attachmentLabels}
            labels={labels}
            fieldName={fieldName}
          />
        ))}
      </div>
    );
  }

  // Check if arrays are equal - show compact summary
  if (JSON.stringify(before) === JSON.stringify(after)) {
    if (after.length === 0) {
      return <EmptyValue />;
    }

    return (
      <span className="text-sm text-gray-500">[{after.length} items]</span>
    );
  }

  // Track which before items have been matched
  const matchedKeys = new Set();

  // Determine if we should ignore record ID when comparing (for experience/hotel/template-matched fields)
  const shouldIgnoreRecordId =
    EXPERIENCE_ID_MATCH_FIELDS.includes(fieldName) ||
    HOTEL_ID_MATCH_FIELDS.includes(fieldName) ||
    TEMPLATE_ID_MATCH_FIELDS.includes(fieldName);

  // Process after items and match with before items
  const afterItems = after.map((item) => {
    const key = isPrimitiveArray ? String(item) : getItemId(item, fieldName);
    let status = 'new';
    let beforeItem = null;

    if (key && beforeLookup.has(key)) {
      matchedKeys.add(key);
      beforeItem = beforeLookup.get(key).item;
      if (areItemsEqual(beforeItem, item, shouldIgnoreRecordId)) {
        status = 'unchanged';
      } else {
        status = 'modified';
      }
    }

    return { item, key, status, beforeItem };
  });

  // Find removed items (in before but not matched)
  const removedItems = before.filter((item) => {
    const key = isPrimitiveArray ? String(item) : getItemId(item, fieldName);
    return key && !matchedKeys.has(key);
  });

  return (
    <div className="space-y-2">
      {afterItems.map(({ item, key, status, beforeItem }, index) => {
        const reactKey = key || index;

        if (status === 'new') {
          // Use MealDisplay for meals
          if (fieldName === 'meals') {
            return (
              <div key={reactKey} className="text-sm">
                <div>
                  <span className="px-1 rounded text-xs shrink-0 mt-0.5 bg-green-100 text-green-700">
                    +
                  </span>
                  <MealDisplay meal={item} isNew />
                </div>
              </div>
            );
          }

          return (
            <div key={reactKey} className="text-sm">
              <div>
                <span className="px-1 rounded text-xs shrink-0 mt-0.5 bg-green-100 text-green-700">
                  +
                </span>
                <div className="flex-1">
                  <SmartItemDisplay
                    item={item}
                    className="text-green-700"
                    isNew
                    attachmentLabels={attachmentLabels}
                    labels={labels}
                    fieldName={fieldName}
                  />
                </div>
              </div>
            </div>
          );
        }

        if (status === 'modified') {
          // Use ProgramDiff for modified programs to show clearer context
          if (isProgram(item)) {
            return (
              <div key={reactKey} className="text-sm">
                <ProgramDiff
                  beforeProgram={beforeItem}
                  afterProgram={item}
                  labels={labels}
                  attachmentLabels={attachmentLabels}
                />
              </div>
            );
          }

          // Use custom diff for suggested options
          if (isProgramSuggestedOption(item)) {
            return (
              <div key={reactKey} className="text-sm">
                <ProgramSuggestedOptionDiff
                  beforeOption={beforeItem}
                  afterOption={item}
                  labels={labels}
                />
              </div>
            );
          }

          // Use TranslationDiffDisplay for modified translations
          if (isTranslation(item)) {
            return (
              <div key={reactKey} className="text-sm">
                <TranslationDiffDisplay
                  beforeTranslation={beforeItem}
                  afterTranslation={item}
                />
              </div>
            );
          }

          // Use SmartItemDisplay with isModified for modified attachments
          if (isAttachment(item)) {
            return (
              <div key={reactKey} className="text-sm">
                <div>
                  <span className="px-1 rounded text-xs shrink-0 mt-0.5 bg-amber-100 text-amber-700">
                    ~
                  </span>
                  <div className="flex-1">
                    <SmartItemDisplay
                      item={item}
                      isModified
                      beforeKind={beforeItem?.kind}
                      attachmentLabels={attachmentLabels}
                      labels={labels}
                      fieldName={fieldName}
                    />
                  </div>
                </div>
              </div>
            );
          }

          // Use PeriodDiff for modified periods
          if (isPeriod(item)) {
            return (
              <div key={reactKey} className="text-sm">
                <PeriodDiff
                  beforePeriod={beforeItem}
                  afterPeriod={item}
                  labels={labels}
                  attachmentLabels={attachmentLabels}
                />
              </div>
            );
          }

          return (
            <div key={reactKey} className="text-sm">
              <div>
                <span className="px-1 rounded text-xs shrink-0 mt-0.5 bg-amber-100 text-amber-700">
                  ~
                </span>
                <div className="flex-1">
                  {isObject(item) ? (
                    <ObjectDiff
                      beforeValue={beforeItem}
                      afterValue={item}
                      depth={depth + 1}
                      attachmentLabels={attachmentLabels}
                      labels={labels}
                      ignoreRecordId={shouldIgnoreRecordId}
                    />
                  ) : (
                    <SmartItemDisplay
                      item={item}
                      attachmentLabels={attachmentLabels}
                      labels={labels}
                      fieldName={fieldName}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        }

        // Unchanged
        // History entries recorded before translations were diffed by locale
        // carry every translation, not just the edited one: rendering the
        // untouched locales buries the actual change.
        if (isTranslation(item)) {
          return null;
        }

        // Use MealDisplay for meals
        if (fieldName === 'meals') {
          return (
            <div key={reactKey} className="text-sm">
              <MealDisplay meal={item} />
            </div>
          );
        }

        return (
          <div key={reactKey} className="text-sm">
            <SmartItemDisplay
              item={item}
              attachmentLabels={attachmentLabels}
              labels={labels}
              fieldName={fieldName}
            />
          </div>
        );
      })}
      {removedItems.map((item, index) => {
        const key = isPrimitiveArray ? String(item) : getItemId(item);

        // Use MealDisplay for meals
        if (fieldName === 'meals') {
          return (
            <div key={`removed-${key || index}`} className="text-sm">
              <div>
                <span className="px-1 rounded text-xs shrink-0 mt-0.5 bg-red-100 text-red-700">
                  -
                </span>
                <div className="opacity-50">
                  <MealDisplay meal={item} isRemoved />
                </div>
              </div>
            </div>
          );
        }

        return (
          <div key={`removed-${key || index}`} className="text-sm">
            <div>
              <span className="px-1 rounded text-xs shrink-0 mt-0.5 bg-red-100 text-red-700">
                -
              </span>
              <div className="flex-1 line-through text-gray-400">
                <SmartItemDisplay
                  item={item}
                  isRemoved
                  attachmentLabels={attachmentLabels}
                  labels={labels}
                  fieldName={fieldName}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
